import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { LoadingSpinner } from '../../ui/Loading';
import { useCreditWalletLedger, useUsdWalletLedger } from '../../../hooks/useApi';
import { formatCurrency, cn } from '../../../utils';
import { isUsdOutflowLedgerType } from '../usdWallets/usdWalletLedgerUtils';

export type WalletPnlOwnerType = 'super_agent' | 'agent' | 'shop';

type OwnerWalletPnlCardsProps = {
  ownerType: WalletPnlOwnerType;
  ownerId: string;
  enabled?: boolean;
};

function PnlStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'in' | 'out' | 'net' | 'neutral';
}) {
  const toneClass =
    tone === 'in'
      ? 'text-emerald-700'
      : tone === 'out'
        ? 'text-red-700'
        : tone === 'net'
          ? 'text-foreground font-semibold'
          : 'text-foreground';
  return (
    <div>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={cn('mt-0.5 tabular-nums text-sm', toneClass)}>{value}</p>
    </div>
  );
}

function signedMoney(n: number, asCurrency = true): string {
  const abs = Math.abs(n);
  const body = asCurrency
    ? formatCurrency(abs)
    : abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n > 0) return `+${body}`;
  if (n < 0) return `−${body}`;
  return body;
}

/**
 * Lifetime-style P&L snapshots for USD + Credit (CDN) wallets by owner.
 * Credit uses ledger summary; USD aggregates the owner ledger page (up to 200 rows).
 */
export const OwnerWalletPnlCards: React.FC<OwnerWalletPnlCardsProps> = ({
  ownerType,
  ownerId,
  enabled = true,
}) => {
  const creditLedger = useCreditWalletLedger(
    { ownerType, ownerId, limit: 1, offset: 0 },
    enabled && !!ownerId,
  );
  const usdLedger = useUsdWalletLedger(
    { ownerType, ownerId, currency: 'USD', limit: 200, offset: 0 },
    enabled && !!ownerId,
  );

  const creditSummary = creditLedger.data?.summary;
  const creditIn = Number(creditSummary?.totalDeposits ?? 0);
  const creditOut = Number(creditSummary?.totalWithdrawals ?? 0);
  const creditNet = Number(creditSummary?.netFlow ?? creditIn - creditOut);

  const usdPnl = useMemo(() => {
    const rows = usdLedger.data?.rows ?? [];
    let moneyIn = 0;
    let moneyOut = 0;
    for (const row of rows) {
      const type = String(row?.type ?? '');
      const amountNet = Math.abs(Number(row?.amountNet ?? row?.amount_net ?? 0));
      const amountGross = Math.abs(Number(row?.amountGross ?? row?.amount_gross ?? 0));
      if (!Number.isFinite(amountNet) && !Number.isFinite(amountGross)) continue;
      if (isUsdOutflowLedgerType(type)) {
        moneyOut += amountGross > 0 ? amountGross : amountNet;
      } else if (type === 'deposit' || type === 'reversal' || type === 'adjustment') {
        if (type === 'deposit') moneyIn += amountNet || amountGross;
        else if (type === 'reversal' || type === 'adjustment') {
          const signed = Number(row?.amountNet ?? row?.amount_net ?? 0);
          if (signed >= 0) moneyIn += Math.abs(signed);
          else moneyOut += Math.abs(signed);
        }
      } else if (type === 'purchase_credits') {
        moneyOut += amountGross > 0 ? amountGross : amountNet;
      }
    }
    return {
      moneyIn,
      moneyOut,
      net: moneyIn - moneyOut,
      rowCount: rows.length,
      totalCount: Number(usdLedger.data?.count ?? rows.length),
    };
  }, [usdLedger.data]);

  const ownerQs = `ownerType=${encodeURIComponent(ownerType)}&ownerId=${encodeURIComponent(ownerId)}`;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <div className="rounded-md border p-3 text-sm space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <p className="font-medium text-foreground">USD wallet P&amp;L</p>
          </div>
          <Link to={`/usd-wallets?${ownerQs}`} className="text-[11px] font-medium text-primary hover:underline">
            Open ledger
          </Link>
        </div>
        {usdLedger.isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : usdLedger.error ? (
          <p className="text-xs text-destructive">Could not load USD P&amp;L.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <PnlStat label="Money in" value={signedMoney(usdPnl.moneyIn)} tone="in" />
              <PnlStat label="Money out" value={signedMoney(-usdPnl.moneyOut)} tone="out" />
              <PnlStat label="Net" value={signedMoney(usdPnl.net)} tone="net" />
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              {usdPnl.net >= 0 ? (
                <TrendingUp className="h-3 w-3 text-emerald-600" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-600" />
              )}
              {usdPnl.totalCount > usdPnl.rowCount
                ? `Approx. from latest ${usdPnl.rowCount} of ${usdPnl.totalCount} ledger rows`
                : `From ${usdPnl.rowCount} ledger row${usdPnl.rowCount === 1 ? '' : 's'}`}
            </p>
          </>
        )}
      </div>

      <div className="rounded-md border p-3 text-sm space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            <p className="font-medium text-foreground">Credit P&amp;L</p>
          </div>
          <Link to={`/credit-wallets?${ownerQs}`} className="text-[11px] font-medium text-primary hover:underline">
            Open ledger
          </Link>
        </div>
        {creditLedger.isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : creditLedger.error ? (
          <p className="text-xs text-destructive">Could not load Credit P&amp;L.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <PnlStat label="Credits in" value={signedMoney(creditIn, false)} tone="in" />
              <PnlStat label="Credits out" value={signedMoney(-creditOut, false)} tone="out" />
              <PnlStat label="Net" value={signedMoney(creditNet, false)} tone="net" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              CDN credit wallet flow · {creditSummary?.totalTransactions ?? 0} transaction
              {(creditSummary?.totalTransactions ?? 0) === 1 ? '' : 's'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OwnerWalletPnlCards;
